import { __rest } from 'tslib';
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { cva } from 'class-variance-authority';

var minValueDisplayStyle = cva([
    'whitespace-nowrap',
    'min-w-[32px] h-8 p-2',
    'absolute top-[-55px]',
    'text-sm text-white bg-blue-301 rounded',
    'flex justify-center items-center',
    'invisible',
    'after:content-[""] after:absolute',
    'after:top-full after:left-1/2',
    'after:border-[10px] after:border-transparent',
    'after:h-0 after:w-0',
    'after:pointer-events-none',
    'after:border-t-blue-301',
    'after:ml-[-10px]',
], {
    variants: {
        showValue: {
            true: '!visible',
        },
        hideValue: {
            true: '!invisible',
        },
    },
    compoundVariants: [
        {
            showValue: false,
            hideValue: false,
            class: 'peer-hover/min:visible',
        },
    ],
    defaultVariants: {
        showValue: false,
        hideValue: false,
    },
});
var maxValueDisplayStyle = cva([
    'whitespace-nowrap',
    'min-w-[32px] h-8 p-2',
    'absolute top-[-55px]',
    'text-sm text-white bg-blue-301 rounded',
    'flex justify-center items-center',
    'invisible',
    'after:content-[""] after:absolute',
    'after:top-full after:left-1/2',
    'after:border-[10px] after:border-transparent',
    'after:h-0 after:w-0',
    'after:pointer-events-none',
    'after:border-t-blue-301',
    'after:ml-[-10px]',
], {
    variants: {
        showValue: {
            true: '!visible',
        },
        hideValue: {
            true: '!invisible',
        },
    },
    compoundVariants: [
        {
            showValue: false,
            hideValue: false,
            class: 'peer-hover/max:visible',
        },
    ],
    defaultVariants: {
        showValue: false,
        hideValue: false,
    },
});
var sliderStyle = cva('pointer-events-none absolute h-0 w-full outline-none', {
    variants: {
        disabled: {
            true: 'appearance-none [&::-webkit-slider-thumb]:disabled:bg-disabled [&::-webkit-slider-thumb]:disabled:border-none [&::-webkit-slider-thumb]:disabled:rounded-[50%] [&::-webkit-slider-thumb]:disabled:cursor-pointer [&::-webkit-slider-thumb]:disabled:h-5 [&::-webkit-slider-thumb]:disabled:w-5 [&::-webkit-slider-thumb]:disabled:mt-1 [&::-webkit-slider-thumb]:disabled:pointer-events-all [&::-webkit-slider-thumb]:disabled:relative [&::-webkit-slider-thumb]:disabled:appearance-none [&::-moz-range-thumb]:disabled:bg-disabled [&::-moz-range-thumb]:disabled:border-none [&::-moz-range-thumb]:disabled:rounded-[50%] [&::-moz-range-thumb]:disabled:cursor-pointer [&::-moz-range-thumb]:disabled:h-5 [&::-moz-range-thumb]:disabled:w-5 [&::-moz-range-thumb]:disabled:mt-1 [&::-moz-range-thumb]:disabled:pointer-events-all [&::-moz-range-thumb]:disabled:relative [&::-moz-range-thumb]:disabled:appearance-none',
            false: 'appearance-none [&::-webkit-slider-thumb]:bg-brand [&::-webkit-slider-thumb]:border-none [&::-webkit-slider-thumb]:rounded-[50%] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:mt-1 [&::-webkit-slider-thumb]:pointer-events-all [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:hover:bg-brand-medium [&::-webkit-slider-thumb]:active:bg-blue-301 [&::-webkit-slider-thumb]:focus-visible:shadow-[0_0_0_1px_black,0_0_0_3px_white,0_0_0_8px_#C8DAEB] [&::-webkit-slider-thumb]:appearance-none [&::-moz-range-thumb]:bg-brand [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:rounded-[50%] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:mt-1 [&::-moz-range-thumb]:pointer-events-all [&::-moz-range-thumb]:relative [&::-moz-range-thumb]:hover:bg-brand-medium [&::-moz-range-thumb]:active:bg-blue-301 [&::-moz-range-thumb]:focus-visible:shadow-[0_0_0_1px_black,0_0_0_3px_white,0_0_0_8px_#C8DAEB] [&::-moz-range-thumb]:appearance-none',
        },
    },
});
var sliderSliderStyle = cva('absolute rounded h-[4px] w-full z-[1]', {
    variants: {
        disabled: {
            true: 'bg-gray-cool-200',
            false: 'bg-gray-200',
        },
    },
    defaultVariants: {
        disabled: false,
    },
});
var sliderSliderActiveStyle = cva('absolute rounded h-[4px] z-[2]', {
    variants: {
        disabled: {
            true: 'bg-disabled',
            false: 'bg-brand peer-hover/min:bg-brand-medium peer-hover/max:bg-brand-medium peer-active/max:bg-blue-301',
        },
    },
});
var textStyle = cva('', {
    variants: {
        disabled: {
            true: 'text-disabled-text',
            false: 'text-light-text',
        },
    },
});
var Slider = function (_a) {
    var label = _a.label, _b = _a.min, min = _b === void 0 ? 0 : _b, _c = _a.max, max = _c === void 0 ? 100 : _c, _d = _a.value, value = _d === void 0 ? 0 : _d, onChange = _a.onChange, disabled = _a.disabled, showValue = _a.showValue, hideValue = _a.hideValue, _e = _a.step, step = _e === void 0 ? 1 : _e, _f = _a.unitLabel, unitLabel = _f === void 0 ? '%' : _f; __rest(_a, ["label", "min", "max", "value", "onChange", "disabled", "showValue", "hideValue", "step", "unitLabel"]);
    var _g = useState(min), minValue = _g[0], setMinValue = _g[1];
    var minValRef = useRef(null);
    var _h = useState(max), maxValue = _h[0], setMaxValue = _h[1];
    var maxValRef = useRef(null);
    var slider = useRef(null);
    var minValTextRef = useRef(null);
    var maxValTextRef = useRef(null);
    var isMultiSlider = useMemo(function () { return Array.isArray(value) && value.length > 1; }, [value]);
    var getPercent = useCallback(function (value) { return Math.round(((value - min) / (max - min)) * 100); }, [min, max]);
    var getLabelPosition = useCallback(function (position) {
        var _a, _b;
        var percentOfRange = 0;
        var sliderWidth = 0;
        var halfDisplayValue = 0;
        var halfThumb = 10;
        if (position === 'min') {
            if (!minValTextRef.current || !minValRef.current)
                return;
            halfDisplayValue = minValTextRef.current.offsetWidth / 2;
            sliderWidth = (_a = minValRef.current.offsetWidth) !== null && _a !== void 0 ? _a : 0;
        }
        else {
            if (!maxValTextRef.current || !maxValRef.current)
                return;
            halfDisplayValue = maxValTextRef.current.offsetWidth / 2;
            sliderWidth = (_b = maxValRef.current.offsetWidth) !== null && _b !== void 0 ? _b : 0;
        }
        var centerPosition = sliderWidth / 2;
        percentOfRange = isMultiSlider
            ? ((position === 'min' ? minValue : maxValue) - min) / (max - min)
            : (position === 'min' ? minValue : maxValue) / (max - min);
        if (isNaN(percentOfRange)) {
            percentOfRange = 0;
        }
        var valuePxPosition = percentOfRange * sliderWidth;
        var distFromCenter = valuePxPosition - centerPosition;
        var percentDistFromCenter = distFromCenter / centerPosition;
        var offset = percentDistFromCenter * halfThumb;
        return valuePxPosition - halfDisplayValue - offset;
    }, [minValue, maxValue]);
    useEffect(function () {
        if (!Array.isArray(value)) {
            setMinValue(value < min ? min : value);
            return;
        }
        if (value.length > 1) {
            setMinValue(value[0] < min ? min : value[0]);
            setMaxValue(value[1] > max ? max : value[1]);
            return;
        }
        setMinValue(value[0] < min ? min : value[0]);
        return;
    }, [value]);
    useEffect(function () {
        if (!slider.current)
            return;
        if (!minValTextRef.current)
            return;
        if (!minValRef.current)
            return;
        var minPercent = 0, maxPercent = 0;
        if (maxValRef.current) {
            minPercent = getPercent(minValue);
            maxPercent = getPercent(+maxValRef.current.value);
        }
        else {
            minPercent = getPercent(0);
            maxPercent = getPercent(+minValue);
        }
        slider.current.style.left = "".concat(minPercent, "%");
        slider.current.style.width = "".concat(maxPercent - minPercent, "%");
        var finalLabelPosition = getLabelPosition('min');
        minValTextRef.current.style.left = "".concat(finalLabelPosition, "px");
    }, [minValue, getPercent, isMultiSlider]);
    useEffect(function () {
        var _a, _b;
        if (!minValRef.current || !isMultiSlider)
            return;
        if (!slider.current)
            return;
        if (!maxValTextRef.current)
            return;
        if (!maxValRef.current)
            return;
        var minPercent = getPercent(+minValRef.current.value);
        var maxPercent = getPercent((_b = +((_a = maxValRef.current) === null || _a === void 0 ? void 0 : _a.value)) !== null && _b !== void 0 ? _b : maxValue);
        slider.current.style.width = "".concat(maxPercent - minPercent, "%");
        var finalLabelPosition = getLabelPosition('max');
        maxValTextRef.current.style.left = "".concat(finalLabelPosition, "px");
    }, [maxValue, getPercent, isMultiSlider]);
    var handleMinChange = function (event) {
        var value = Math.min(+event.target.value, isMultiSlider ? max - 1 : max);
        isMultiSlider && value < maxValue
            ? step ? setMinValue(value % step === 0 ? value : minValue) : setMinValue(value)
            : !isMultiSlider && value <= maxValue && setMinValue(value);
        event.target.value = value.toString();
        onChange === null || onChange === void 0 ? void 0 : onChange({ min: value, max: maxValue }, event);
    };
    var handleMaxChange = function (event) {
        var value = Math.max(+event.target.value, minValue + 1);
        step ? setMaxValue(value % step === 0 ? value : maxValue) : setMaxValue(value);
        event.target.value = value.toString();
        onChange === null || onChange === void 0 ? void 0 : onChange({ min: minValue, max: value }, event);
    };
    return (React.createElement("div", { className: 'flex flex-col gap-3' },
        React.createElement("div", { className: 'flex justify-between' },
            React.createElement("label", { className: textStyle({ disabled: !!disabled }) }, label),
            React.createElement("span", { className: textStyle({ disabled: !!disabled }) }, unitLabel)),
        React.createElement("div", { className: 'relative w-full' },
            React.createElement(React.Fragment, null,
                React.createElement("input", { type: 'range', min: min, max: max, value: minValue, ref: minValRef, onChange: handleMinChange, step: step, className: sliderStyle({
                        disabled: !!disabled,
                        className: "peer/min z-[3] ".concat(minValue > max - 100 ? 'z-[5]' : ''),
                    }), disabled: disabled, tabIndex: 0, "data-testid": 'minRangeThumb' }),
                React.createElement("div", { "data-testid": 'minRangeValueText', ref: minValTextRef, className: minValueDisplayStyle({ showValue: showValue, hideValue: hideValue }), style: { position: 'absolute', bottom: '10px' } }, minValue)),
            isMultiSlider && (React.createElement(React.Fragment, null,
                React.createElement("input", { key: max, id: 'min-test-value', type: 'range', min: min, max: max, value: maxValue, ref: maxValRef, onChange: handleMaxChange, className: sliderStyle({ disabled: !!disabled, className: 'peer/max z-[4]' }), disabled: disabled, tabIndex: 0, "data-testid": 'maxRangeThumb', step: step }),
                React.createElement("div", { "data-testid": 'maxRangeValueText', ref: maxValTextRef, className: maxValueDisplayStyle({ showValue: showValue, hideValue: hideValue }) }, maxValue))),
            React.createElement("div", { className: sliderSliderStyle({ disabled: !!disabled }) }),
            React.createElement("div", { ref: slider, className: sliderSliderActiveStyle({ disabled: !!disabled }) })),
        React.createElement("div", { className: 'flex justify-between' },
            React.createElement("span", { className: textStyle({ disabled: !!disabled }) }, min),
            React.createElement("span", { className: textStyle({ disabled: !!disabled }) }, max))));
};

export { Slider };
